import React, { useRef, useCallback, PropsWithChildren } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RouteComponentProps } from 'react-router';

import get from 'lodash/get';

import { SubActions, makePermissionCheckerForCurrentSub } from 'Redux/thunks/subscriptions.thunks';
import { DatahubActions } from '../redux-store/thunks/datahub.thunks';
import { canEditVPN as canEditVPNFn } from 'Redux/selectors';

import useAsync from '../hooks/useAsync';

import Page from '../components/page';
import Button from 'Common/Button/Button';
import PageHeader, { PageHeaderTitle } from '../components/page-header';
import PortalFallbackMessage from 'Common/PortalFallbackMessage/PortalFallbackMessage';
import { TabbedNav } from '../components/tabbed-nav/TabbedNav.view';

import { TestID } from '../testID';

import type { RootState } from '../types/state.types';
import type { DataEntityTab } from 'src/types/dataEntitiesTabs.types';

type RegisteredRefreshesFns = () => void;

export const SubscriptionSettingsPage: React.FC<PropsWithChildren<RouteComponentProps<{}, any>>> = (
  props
) => {
  const { params, children } = props;
  const { subId } = params;

  const registeredRefreshes = useRef<RegisteredRefreshesFns[]>([]);

  const dispatch = useDispatch();

  const connected = useSelector((state: RootState) => state.subscription.connected);
  const currentSubscription = useSelector((state: RootState) =>
    state.subscriptions.find((s) => s.id === state.subscription.id)
  );
  const user = useSelector((state: RootState) => state.user);
  const permissionChecker = useSelector((state: RootState) =>
    makePermissionCheckerForCurrentSub(state)
  );
  const canEditVPN = useSelector((state: RootState) => canEditVPNFn(state));
  const isPortalInFallbackMode = useSelector(
    (state: RootState) => state.globals.status.is_fallback_mode
  );
  const isPortalAdmin = useSelector((state: RootState) => state.user.isPortalAdmin);

  const refreshSubscription = (subId: string) => dispatch(SubActions.refresh(subId));
  const refreshDatahubInfo = () => dispatch(DatahubActions.getInfo());

  const _refresh = useCallback(() => {
    refreshSubscription(subId);

    if (connected) {
      refreshDatahubInfo();
    }

    registeredRefreshes.current.forEach((cb) => cb());
  }, [props]);

  const [refresh] = useAsync(_refresh);

  const handleRefresh = () => {
    refresh();
  };

  const registerRefresh = useCallback((func: () => void) => {
    registeredRefreshes.current.push(func);
  }, []);

  const unregisterRefresh = useCallback((func: () => void) => {
    registeredRefreshes.current = registeredRefreshes.current.filter((c) => c !== func);
  }, []);

  const isPaymentMethodOwner = () => {
    const paymentMethodInfo = currentSubscription?.paymentmethod_info;
    const paymentOwnerUserId = get(paymentMethodInfo, 'owner.user_id');
    const userId = get(user, 'user_id');

    if (paymentOwnerUserId) {
      if (paymentOwnerUserId === userId) return true;
    }

    return false;
  };

  let nav: DataEntityTab[] = [
    {
      id: 'basic',
      label: 'Basics',
      tooltip: 'Basic settings tab',
      to: `/subscription/${subId}/settings-subscription/basics`,
      testId: TestID.SubscriptionsSettingsBasicTab,
    },
  ];

  if (permissionChecker('modify_subscription')) {
    nav.push({
      id: 'license',
      label: 'License',
      tooltip: 'License settings tab',
      to: `/subscription/${subId}/settings-subscription/license`,
      testId: TestID.SubscriptionsSettingsLicenseTab,
    });
  }

  nav.push({
    id: 'network',
    label: 'Network',
    tooltip: 'Network settings tab',
    to: `/subscription/${subId}/settings-subscription/network`,
    testId: TestID.SubscriptionsSettingsNetworkTab,
  });

  if ((permissionChecker('modify_subscription') && isPaymentMethodOwner()) || isPortalAdmin) {
    nav.push({
      id: 'products',
      label: 'Products',
      tooltip: 'Products settings tab',
      to: `/subscription/${subId}/settings-subscription/products`,
      testId: TestID.SubscriptionsSettingsProductTab,
    });
  }

  if (canEditVPN) {
    nav.push({
      id: 'vpn',
      label: 'VPN',
      tooltip: 'VPN settings tab',
      to: `/subscription/${subId}/settings-subscription/vpn`,
      testId: TestID.SubscriptionsSettingsVPNTab,
    });

    nav.push({
      id: 'dns',
      label: 'DNS',
      tooltip: 'DNS forwarding settings tab',
      to: `/subscription/${subId}/settings-subscription/dns`,
      testId: TestID.SubscriptionsSettingsDNSTab,
    });
  }

  if (permissionChecker('view_api_jwt_tokens')) {
    nav.push({
      id: 'jwt',
      label: 'JWT',
      tooltip: 'JWT settings tab',
      to: `/subscription/${subId}/settings-subscription/jwt`,
      testId: TestID.SubscriptionsSettingsJWTTab,
    });
  }

  nav = nav.concat([
    {
      id: 'roles',
      label: 'Roles',
      tooltip: 'Roles settings tab',
      to: `/subscription/${subId}/settings-subscription/roles`,
      testId: TestID.SubscriptionsSettingsRolesTab,
    },
    {
      id: 'members',
      label: 'Members',
      tooltip: 'Members settings tab',
      to: `/subscription/${subId}/settings-subscription/members`,
      testId: TestID.SubscriptionsSettingsMembersTab,
    },
  ]);

  return (
    // @ts-ignore
    <Page>
      <PageHeader style={{ alignItems: 'baseline' }}>
        <PageHeaderTitle>Subscription settings</PageHeaderTitle>
        {/* @ts-ignore */}
        {isPortalInFallbackMode && <PortalFallbackMessage />}
        <Button theme="primary" style={{ marginLeft: 'auto' }} onClick={handleRefresh}>
          Refresh
        </Button>
      </PageHeader>
      <TabbedNav nav={nav}>
        {
          // @ts-ignore
          React.cloneElement(children, {
            registerRefresh: registerRefresh,
            unregisterRefresh: unregisterRefresh,
          })
        }
      </TabbedNav>
    </Page>
  );
};
